import { Trans, useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';

import { Typography } from '@remarkable/ark-web';
import type { ColumnDef } from '@tanstack/react-table';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import type {
  CloudConnectPayment,
  ConnectInterval,
} from 'src/api/endpoints/cloudConnect.types';
import { useCloudConnectPaymentHistory } from 'src/api/queries';
import { NotificationBox, Spinner, Table, Tooltip } from 'src/components';
import { TablePagination } from 'src/components/TablePagination';
import { formatShortDate } from 'src/utils/formatDate';
import { formatAmount } from 'src/utils/productUtils';
import { useWindowSize } from 'src/utils/useWindowSize';

import { CloudConnectReceiptLink } from './CloudConnectReceiptLink';

const columnHelper = createColumnHelper<CloudConnectPayment>();

const paymentDescription = (
  payment: CloudConnectPayment,
  labels: Record<ConnectInterval, string>
) => labels[payment.interval];

const paymentTotal = (payment: CloudConnectPayment, locale: string) =>
  formatAmount(payment.amount.totalAmount, payment.amount.currency, locale);

export const CloudConnectPaymentHistoryList = ({
  showSeatsColumn = false,
}: {
  showSeatsColumn?: boolean;
}) => {
  const { t, i18n } = useLingui();
  const paymentHistory = useCloudConnectPaymentHistory();
  const windowSize = useWindowSize();

  const shouldRenderSeatsColumn = showSeatsColumn && windowSize.width > 540;

  const columns = useMemo(() => {
    const labels = {
      monthly: t`Connect monthly`,
      annual: t`Connect annually`,
      biennial: t`Connect 2-year`,
    } satisfies Record<ConnectInterval, string>;

    return [
      columnHelper.accessor('createdAt', {
        header: t`Date`,
        meta: { colWidth: 'w-[110px]' },
        sortingFn: (a, b) =>
          new Date(a.original.createdAt).getTime() -
          new Date(b.original.createdAt).getTime(),
        cell(info) {
          return (
            <Tooltip
              title={formatShortDate(info.row.original.createdAt, {
                showTime: true,
                showYear: true,
              })}
            >
              <Typography variant="body-sm" className="truncate">
                {formatShortDate(info.row.original.createdAt, {
                  showYear: true,
                })}
              </Typography>
            </Tooltip>
          );
        },
      }),
      columnHelper.accessor((payment) => paymentDescription(payment, labels), {
        id: 'description',
        header: t`Description`,
      }),
      shouldRenderSeatsColumn &&
        columnHelper.accessor(() => 1, {
          id: 'seats',
          header: t`Members`,
        }),
      columnHelper.accessor((payment) => paymentTotal(payment, i18n.locale), {
        id: 'total',
        header: t`Total`,
        meta: { colWidth: 'w-1/4' },
      }),
      columnHelper.display({
        header: t`Download`,
        meta: {
          align: 'center',
          colWidth: 'w-64',
          truncate: false,
        },
        cell: (info) => <CloudConnectReceiptLink payment={info.row.original} />,
      }),
    ].filter(Boolean) as ColumnDef<CloudConnectPayment>[];
  }, [i18n.locale, shouldRenderSeatsColumn, t]);

  const table = useReactTable({
    columns,
    initialState: {
      pagination: {
        pageSize: 8,
      },
    },
    data: paymentHistory.data ?? [],
    getRowId: ({ createdAt }) => createdAt,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    renderFallbackValue: () => '-',
  });

  if (paymentHistory.isPending) {
    return <Spinner />;
  }

  if (paymentHistory.isError) {
    return (
      <NotificationBox
        title={t`Failed to load your payment history`}
        error={paymentHistory.error}
      >
        <Typography variant="body-sm" as="p">
          <Trans>Sorry about the inconvenience. Please try again later.</Trans>
        </Typography>
      </NotificationBox>
    );
  }

  if (paymentHistory.data?.length === 0) {
    return (
      <NotificationBox
        title={t`You haven't made any payments yet`}
        variant="info"
        data-cy="no-payments-notification"
      >
        <Typography variant="body-sm" as="p">
          <Trans>
            Any purchases that you make while using this account will appear
            here.
          </Trans>
        </Typography>
      </NotificationBox>
    );
  }

  return (
    <>
      <Table
        table={table}
        data-cy="payment-history-table"
        className="hidden ls:table"
        pagination
      />

      <ul
        data-cy="payment-history-list"
        className="border-t border-gray-100 ls:hidden"
      >
        {table.getRowModel().rows.map((row) => {
          const labels = {
            monthly: t`Connect monthly`,
            annual: t`Connect annually`,
            biennial: t`Connect 2-year`,
          } satisfies Record<ConnectInterval, string>;

          return (
            <li
              data-cy={row.id}
              key={row.id}
              className="border-b border-gray-100 py-12"
            >
              <div className="flex items-center justify-between gap-16">
                <div className="flex min-w-0 flex-col">
                  <Typography variant="body-sm" className="truncate">
                    {formatShortDate(row.original.createdAt, {
                      showYear: true,
                    })}
                  </Typography>

                  <Typography variant="body-sm" className="italic">
                    {paymentDescription(row.original, labels)}
                    <span>
                      {' ⋅ '}
                      {paymentTotal(row.original, i18n.locale)}
                    </span>
                  </Typography>
                </div>

                <CloudConnectReceiptLink payment={row.original} />
              </div>
            </li>
          );
        })}
      </ul>

      <TablePagination table={table} compact className="ls:hidden" />
    </>
  );
};
